import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductImageGallery from "@/components/ProductImageGallery.jsx";
import OrderFilterToolbox from "@/components/OrderFilterToolbox.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import OrderProgressTracker from "@/components/OrderProgressTracker.jsx";
import OrderThumbnail from "@/components/OrderThumbnail.jsx";
import "@/components/order-progress.css";
import {
    deleteAdminOrder,
    downloadAdminOrderReceipt,
    getAdminOrder,
    getAdminOrders,
    getProductLots,
    searchAdminProducts,
    updateAdminOrder,
    updateAdminOrderStatus
} from "@/api/admin.api.js";
import { EMPTY_ORDER_FILTERS, orderFiltersWithCustomer } from "@/utils/orderFilters.js";
import { EMPTY_PRODUCT_FILTERS } from "@/utils/productFilters.js";
import {
    canDownloadOrderReceipt,
    formatFulfillmentType,
    formatOrderStatus,
    formatPrice,
    formatPurchaseDate,
    ORDER_STATUS_OPTIONS,
    ORDER_STATUSES,
    orderStatusClassName
} from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { lowestProductPrice } from "@/utils/productPricing.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { canDeleteOrder, canSeeCostPrice } from "@/utils/staffAccess.js";

const emptyOrderForm = {
    fulfillmentType: "PICKUP",
    status: ORDER_STATUSES.PENDING,
    deliverySummary: "",
    lines: [],
    stockAllocations: []
};

function mapOrderLinesToForm(lines) {
    return (lines || []).map((line) => ({
        productId: line.productId,
        title: line.title || "Produit",
        quantity: line.quantity || 1,
        unitPrice: line.unitPrice ?? 0,
        photoId: line.photoId || line.photoIds?.[0] || null
    }));
}

function mapStockAllocationsToForm(allocations) {
    return (allocations || []).map((allocation) => ({
        productId: allocation.productId,
        lotId: allocation.lotId || "",
        quantity: allocation.quantity || 1
    }));
}

function computeLinesTotal(lines) {
    return (lines || []).reduce(
        (sum, line) => sum + Number(line.unitPrice || 0) * Number(line.quantity || 0),
        0
    );
}

function allocatedTotalForProduct(allocations, productId) {
    return (allocations || [])
        .filter((row) => row.productId === productId)
        .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
}

function validateStockAllocations(lines, allocations, lotsByProduct, lotAvailability) {
    for (const line of lines) {
        const allocated = allocatedTotalForProduct(allocations, line.productId);
        if (allocated !== Number(line.quantity)) {
            return `Sélectionnez les lots pour « ${line.title} » (alloué : ${allocated}, requis : ${line.quantity})`;
        }
    }
    if ((allocations || []).some((row) => !row.lotId || !row.quantity)) {
        return "Chaque allocation de lot doit indiquer un lot et une quantité";
    }
    const qtyByLot = {};
    for (const row of allocations) {
        const qty = Number(row.quantity) || 0;
        qtyByLot[row.lotId] = (qtyByLot[row.lotId] || 0) + qty;
    }
    for (const [lotId, qty] of Object.entries(qtyByLot)) {
        let lot = null;
        for (const lots of Object.values(lotsByProduct)) {
            lot = (lots || []).find((item) => item.id === lotId) || null;
            if (lot) {
                break;
            }
        }
        if (!lot) {
            return "Un lot sélectionné est introuvable. Rechargez la commande et réessayez.";
        }
        const available = lotAvailability(lot);
        if (qty > available) {
            return `Le lot « ${lot.name} » n'a que ${available} article(s) disponible(s) (demandé : ${qty})`;
        }
    }
    return "";
}

function seedAllocationsFromLines(lines, lotsByProduct) {
    const seeded = [];
    for (const line of lines || []) {
        const lots = lotsByProduct[line.productId] || [];
        const preferred = lots.find((lot) => (Number(lot.remainingQuantity) || 0) > 0) || lots[0];
        if (!preferred) {
            continue;
        }
        seeded.push({
            productId: line.productId,
            lotId: preferred.id,
            quantity: Number(line.quantity) || 1
        });
    }
    return seeded;
}

function formatLotOptionLabel(lot, available, includeCost = true) {
    const count = Number(available) || 0;
    const countLabel = count === 1 ? "1 article dans le lot" : `${count} articles dans le lot`;
    if (!includeCost) {
        return `${lot.name || "Lot"} — ${countLabel}`;
    }
    const cost = lot.costPrice == null || lot.costPrice === "" ? 0 : lot.costPrice;
    return `${lot.name || "Lot"} — ${countLabel} — prix de revient ${formatPrice(cost)}`;
}

export default function AdminOrdersSection({ setMessage, setError }) {
    const { role } = useAuth();
    const showCostPrice = canSeeCostPrice(role);
    const allowOrderDelete = canDeleteOrder(role);
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [viewMode, setViewMode] = useState("list");
    const [form, setForm] = useState(emptyOrderForm);
    const [saving, setSaving] = useState(false);
    const [downloadingReceipt, setDownloadingReceipt] = useState(false);
    const [pendingDeleteOrderId, setPendingDeleteOrderId] = useState(null);
    const [productQuery, setProductQuery] = useState("");
    const [productResults, setProductResults] = useState([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const [lotsByProduct, setLotsByProduct] = useState({});
    const [lotsLoading, setLotsLoading] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    const [completePanelOpen, setCompletePanelOpen] = useState(false);
    const [statusAllocations, setStatusAllocations] = useState([]);
    const [draftFilters, setDraftFilters] = useState(() => orderFiltersWithCustomer(searchParams.get("customer")));
    const [appliedFilters, setAppliedFilters] = useState(() => orderFiltersWithCustomer(searchParams.get("customer")));
    const appliedFiltersRef = useRef(appliedFilters);
    appliedFiltersRef.current = appliedFilters;
    const detailRequestIdRef = useRef(0);

    const editTotal = useMemo(() => computeLinesTotal(form.lines), [form.lines]);
    const needsStockAllocation = form.status === ORDER_STATUSES.COMPLETED;
    const lineProductIds = useMemo(
        () => form.lines.map((line) => line.productId).filter(Boolean).join(","),
        [form.lines]
    );
    const viewLineProductIds = useMemo(
        () => (selectedOrder?.lines || []).map((line) => line.productId).filter(Boolean).join(","),
        [selectedOrder]
    );

    useEffect(() => {
        const loadForEdit = viewMode === "edit" && needsStockAllocation && lineProductIds;
        const loadForComplete = completePanelOpen && viewLineProductIds;
        if (!loadForEdit && !loadForComplete) {
            return undefined;
        }
        let cancelled = false;
        const productIds = (loadForComplete ? viewLineProductIds : lineProductIds).split(",");
        (async () => {
            setLotsLoading(true);
            try {
                const entries = await Promise.all(productIds.map(async (productId) => {
                    const response = await getProductLots(productId);
                    const allocatable = (response.data || []).filter((lot) => {
                        const status = lot.status || "READY";
                        return (status === "CREATED" || status === "READY")
                            && (Number(lot.remainingQuantity) || 0) > 0;
                    });
                    return [productId, allocatable];
                }));
                if (!cancelled) {
                    setLotsByProduct(Object.fromEntries(entries));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                }
            } finally {
                if (!cancelled) {
                    setLotsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [viewMode, needsStockAllocation, lineProductIds, completePanelOpen, viewLineProductIds, setError]);

    // Seed one allocation row per line when marking COMPLETED and none exist yet.
    useEffect(() => {
        if (viewMode !== "edit" || !needsStockAllocation || lotsLoading) {
            return;
        }
        setForm((prev) => {
            if (prev.status !== ORDER_STATUSES.COMPLETED || (prev.stockAllocations || []).length > 0) {
                return prev;
            }
            const seeded = seedAllocationsFromLines(prev.lines, lotsByProduct);
            if (!seeded.length) {
                return prev;
            }
            return { ...prev, stockAllocations: seeded };
        });
    }, [viewMode, needsStockAllocation, lotsByProduct, lotsLoading]);

    useEffect(() => {
        if (!completePanelOpen || lotsLoading) {
            return;
        }
        setStatusAllocations((prev) => {
            if ((prev || []).length > 0) {
                return prev;
            }
            return seedAllocationsFromLines(selectedOrder?.lines, lotsByProduct);
        });
    }, [completePanelOpen, lotsLoading, lotsByProduct, selectedOrder]);

    const lotAvailability = useCallback((lot) => {
        const remaining = Number(lot.remainingQuantity) || 0;
        if (selectedOrder?.status !== ORDER_STATUSES.COMPLETED) {
            return remaining;
        }
        const alreadyTaken = (selectedOrder.stockAllocations || [])
            .filter((row) => row.lotId === lot.id)
            .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
        return remaining + alreadyTaken;
    }, [selectedOrder]);

    const addAllocationRow = (productId) => {
        const lots = lotsByProduct[productId] || [];
        const defaultLot = lots.find((lot) => lotAvailability(lot) > 0) || lots[0];
        setForm((prev) => ({
            ...prev,
            stockAllocations: [
                ...prev.stockAllocations,
                {
                    productId,
                    lotId: defaultLot?.id || "",
                    quantity: 1
                }
            ]
        }));
    };

    const updateAllocationRow = (index, field, value) => {
        if (field === "quantity") {
            if (value !== "" && !/^\d+$/.test(value)) {
                return;
            }
        }
        setForm((prev) => ({
            ...prev,
            stockAllocations: prev.stockAllocations.map((row, rowIndex) => (
                rowIndex === index ? { ...row, [field]: field === "quantity" && value !== "" ? Number(value) : value } : row
            ))
        }));
        setError("");
    };

    const commitAllocationQuantity = (index) => {
        setForm((prev) => ({
            ...prev,
            stockAllocations: prev.stockAllocations.map((row, rowIndex) => {
                if (rowIndex !== index) {
                    return row;
                }
                const parsed = Number(row.quantity);
                return {
                    ...row,
                    quantity: Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1
                };
            })
        }));
    };

    const removeAllocationRow = (index) => {
        setForm((prev) => ({
            ...prev,
            stockAllocations: prev.stockAllocations.filter((_, rowIndex) => rowIndex !== index)
        }));
    };

    const handleDownloadReceipt = async () => {
        if (!selectedOrder?.orderId) {
            return;
        }
        setDownloadingReceipt(true);
        setMessage("");
        setError("");
        try {
            await downloadAdminOrderReceipt(selectedOrder.orderId);
            setMessage("Téléchargement du reçu démarré");
        } catch (err) {
            setError(err.message);
        } finally {
            setDownloadingReceipt(false);
        }
    };

    useEffect(() => {
        if (viewMode !== "edit") {
            setProductQuery("");
            setProductResults([]);
            return undefined;
        }
        const query = productQuery.trim();
        if (query.length < 2) {
            setProductResults([]);
            return undefined;
        }
        const timer = setTimeout(() => {
            setProductSearchLoading(true);
            searchAdminProducts({ ...EMPTY_PRODUCT_FILTERS, search: query, status: "READY" }, 0)
                .then((response) => setProductResults(response.data || []))
                .catch(() => setProductResults([]))
                .finally(() => setProductSearchLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [productQuery, viewMode]);

    const applyFilters = (nextFilters) => {
        const filtersToApply = nextFilters ?? draftFilters;
        if (nextFilters) {
            setDraftFilters(nextFilters);
        }
        setAppliedFilters({ ...filtersToApply });
    };

    const resetFilters = () => {
        setDraftFilters(EMPTY_ORDER_FILTERS);
        setAppliedFilters(EMPTY_ORDER_FILTERS);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("customer");
            return next;
        }, { replace: true });
    };

    const customerFilterFromUrl = searchParams.get("customer") || "";

    useEffect(() => {
        if (!customerFilterFromUrl) {
            return;
        }
        const filters = orderFiltersWithCustomer(customerFilterFromUrl);
        setDraftFilters(filters);
        setAppliedFilters(filters);
    }, [customerFilterFromUrl]);

    const loadOrders = useCallback(async (filters) => {
        setLoading(true);
        try {
            const response = await getAdminOrders(filters);
            setOrders(response.data || []);
        } catch (err) {
            setError(err.message);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [setError]);

    useEffect(() => {
        loadOrders(appliedFilters);
    }, [loadOrders, appliedFilters]);

    const openOrderView = async (orderId) => {
        if (detailLoading) {
            return;
        }
        const requestId = ++detailRequestIdRef.current;
        setDetailLoading(true);
        setMessage("");
        setError("");
        try {
            const response = await getAdminOrder(orderId);
            if (requestId !== detailRequestIdRef.current) {
                return;
            }
            if (!response?.data) {
                setError("Commande introuvable");
                return;
            }
            setSelectedOrder(response.data);
            setViewMode("view");
            setForm(emptyOrderForm);
        } catch (err) {
            if (requestId !== detailRequestIdRef.current) {
                return;
            }
            setError(err.message);
        } finally {
            if (requestId === detailRequestIdRef.current) {
                setDetailLoading(false);
            }
        }
    };

    const orderIdFromUrl = searchParams.get("orderId") || "";
    const openedOrderFromUrlRef = useRef(null);

    useEffect(() => {
        if (!orderIdFromUrl || openedOrderFromUrlRef.current === orderIdFromUrl) {
            return;
        }
        openedOrderFromUrlRef.current = orderIdFromUrl;
        openOrderView(orderIdFromUrl).finally(() => {
            setSearchParams((prev) => {
                if (!prev.get("orderId")) {
                    return prev;
                }
                const next = new URLSearchParams(prev);
                next.delete("orderId");
                return next;
            }, { replace: true });
        });
        // Intentionally open once per orderId from the URL (Telegram deep link).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderIdFromUrl]);

    const startEditFromOrder = (order) => {
        setSelectedOrder(order);
        setForm({
            fulfillmentType: order.fulfillmentType || "PICKUP",
            status: order.status || ORDER_STATUSES.PENDING,
            deliverySummary: order.deliverySummary || "",
            lines: mapOrderLinesToForm(order.lines),
            stockAllocations: mapStockAllocationsToForm(order.stockAllocations)
        });
        setLotsByProduct({});
        setProductQuery("");
        setProductResults([]);
        setMessage("");
        setError("");
        setViewMode("edit");
    };

    const startEdit = () => {
        if (!selectedOrder) {
            return;
        }
        startEditFromOrder(selectedOrder);
    };

    const openOrderEdit = async (orderId) => {
        if (detailLoading) {
            return;
        }
        const requestId = ++detailRequestIdRef.current;
        setDetailLoading(true);
        setMessage("");
        setError("");
        try {
            const response = await getAdminOrder(orderId);
            if (requestId !== detailRequestIdRef.current) {
                return;
            }
            if (!response?.data) {
                throw new Error("Détail de commande introuvable");
            }
            startEditFromOrder(response.data);
        } catch (err) {
            if (requestId !== detailRequestIdRef.current) {
                return;
            }
            setError(err.message);
        } finally {
            if (requestId === detailRequestIdRef.current) {
                setDetailLoading(false);
            }
        }
    };

    const cancelEdit = () => {
        setViewMode("view");
        setError("");
    };

    const backToList = () => {
        setSelectedOrder(null);
        setViewMode("list");
        setError("");
    };

    const updateLineQuantity = (productId, raw) => {
        if (raw === "") {
            setForm((prev) => ({
                ...prev,
                lines: prev.lines.map((line) => (
                    line.productId === productId ? { ...line, quantity: "" } : line
                ))
            }));
            setError("");
            return;
        }
        if (!/^\d+$/.test(raw)) {
            return;
        }
        setForm((prev) => ({
            ...prev,
            lines: prev.lines.map((line) => (
                line.productId === productId ? { ...line, quantity: Number(raw) } : line
            ))
        }));
        setError("");
    };

    const commitLineQuantity = (productId) => {
        setForm((prev) => ({
            ...prev,
            lines: prev.lines.map((line) => {
                if (line.productId !== productId) {
                    return line;
                }
                const parsed = Number(line.quantity);
                return {
                    ...line,
                    quantity: Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1
                };
            })
        }));
    };

    const removeLine = (productId) => {
        setForm((prev) => {
            if (prev.lines.length <= 1) {
                setError("La commande doit contenir au moins un article");
                return prev;
            }
            setError("");
            return {
                ...prev,
                lines: prev.lines.filter((line) => line.productId !== productId),
                stockAllocations: prev.stockAllocations.filter((row) => row.productId !== productId)
            };
        });
    };

    const addProductToOrder = (product) => {
        if (!product?.id) {
            return;
        }
        setForm((prev) => {
            if (prev.lines.some((line) => line.productId === product.id)) {
                setError("Ce produit est déjà dans la commande");
                return prev;
            }
            setError("");
            setProductQuery("");
            setProductResults([]);
            return {
                ...prev,
                lines: [
                    ...prev.lines,
                    {
                        productId: product.id,
                        title: product.title || "Produit",
                        quantity: 1,
                        unitPrice: product.price ?? 0,
                        photoId: product.photoId || product.photoIds?.[0] || null
                    }
                ]
            };
        });
    };

    const saveOrder = async (event) => {
        event.preventDefault();
        if (!selectedOrder) {
            return;
        }
        if (!form.lines.length) {
            setError("La commande doit contenir au moins un article");
            return;
        }
        if (form.lines.some((line) => {
            const qty = Number(line.quantity);
            return !Number.isFinite(qty) || qty < 1;
        })) {
            setError("Chaque article doit avoir une quantité d'au moins 1");
            return;
        }
        let stockAllocations;
        if (form.status === ORDER_STATUSES.COMPLETED) {
            const allocationError = validateStockAllocations(
                form.lines,
                form.stockAllocations,
                lotsByProduct,
                lotAvailability
            );
            if (allocationError) {
                setError(allocationError);
                return;
            }
            stockAllocations = form.stockAllocations.map((row) => ({
                productId: row.productId,
                lotId: row.lotId,
                quantity: Number(row.quantity)
            }));
        }
        setSaving(true);
        setMessage("");
        setError("");
        try {
            const response = await updateAdminOrder(selectedOrder.orderId, {
                fulfillmentType: form.fulfillmentType,
                status: form.status,
                deliverySummary: form.deliverySummary.trim(),
                lines: form.lines.map((line) => ({
                    productId: line.productId,
                    quantity: Number(line.quantity),
                    unitPrice: Number(line.unitPrice)
                })),
                ...(stockAllocations ? { stockAllocations } : {})
            });
            setSelectedOrder(response.data);
            setViewMode("view");
            setMessage("Commande mise à jour");
            await loadOrders(appliedFiltersRef.current);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        setMessage("");
        setError("");
        try {
            await deleteAdminOrder(orderId);
            setPendingDeleteOrderId(null);
            setMessage("Commande supprimée");
            if (selectedOrder?.orderId === orderId) {
                setSelectedOrder(null);
                setViewMode("list");
            }
            await loadOrders(appliedFiltersRef.current);
        } catch (err) {
            setPendingDeleteOrderId(null);
            setError(err.message);
        }
    };

    const closeCompletePanel = () => {
        setCompletePanelOpen(false);
        setStatusAllocations([]);
    };

    const applyStatusChange = async (status, stockAllocations) => {
        if (!selectedOrder) {
            return;
        }
        setStatusSaving(true);
        setMessage("");
        setError("");
        try {
            const response = await updateAdminOrderStatus(selectedOrder.orderId, {
                status,
                ...(stockAllocations ? { stockAllocations } : {})
            });
            setSelectedOrder(response.data);
            closeCompletePanel();
            setMessage("Statut de la commande mis à jour");
            await loadOrders(appliedFiltersRef.current);
        } catch (err) {
            setError(err.message);
        } finally {
            setStatusSaving(false);
        }
    };

    const handleStatusSelect = async (nextStatus) => {
        if (!selectedOrder || nextStatus === selectedOrder.status) {
            return;
        }
        if (nextStatus === ORDER_STATUSES.COMPLETED) {
            setStatusAllocations(mapStockAllocationsToForm(selectedOrder.stockAllocations));
            setCompletePanelOpen(true);
            setError("");
            return;
        }
        await applyStatusChange(nextStatus);
    };

    const confirmCompleteWithAllocations = async () => {
        if (!selectedOrder) {
            return;
        }
        const lines = (selectedOrder.lines || []).map((line) => ({
            productId: line.productId,
            title: line.title || "Produit",
            quantity: line.quantity || 1
        }));
        const allocationError = validateStockAllocations(
            lines,
            statusAllocations,
            lotsByProduct,
            lotAvailability
        );
        if (allocationError) {
            setError(allocationError);
            return;
        }
        await applyStatusChange(
            ORDER_STATUSES.COMPLETED,
            statusAllocations.map((row) => ({
                productId: row.productId,
                lotId: row.lotId,
                quantity: Number(row.quantity)
            }))
        );
    };

    const buildOrderMenuItems = (order, { includeView = true } = {}) => {
        const items = [];
        if (includeView) {
            items.push({
                id: "view",
                label: "Voir le détail",
                onClick: () => openOrderView(order.orderId)
            });
        }
        if (!includeView && canDownloadOrderReceipt(order, { customerDownloadEnabled: true })) {
            items.push({
                id: "download-receipt",
                label: downloadingReceipt ? "Téléchargement…" : "Télécharger le reçu (PDF)",
                onClick: () => {
                    if (!downloadingReceipt) {
                        handleDownloadReceipt();
                    }
                }
            });
        }
        items.push({
            id: "edit",
            label: "Modifier",
            onClick: () => (includeView ? openOrderEdit(order.orderId) : startEdit())
        });
        if (allowOrderDelete) {
            items.push({
                id: "delete",
                label: "Supprimer",
                danger: true,
                onClick: () => setPendingDeleteOrderId(order.orderId)
            });
        }
        return items;
    };

    const pendingDeleteOrder = orders.find((order) => order.orderId === pendingDeleteOrderId)
        || (selectedOrder?.orderId === pendingDeleteOrderId ? selectedOrder : null);
    const pendingDeleteLabel = pendingDeleteOrder?.previewTitle
        || (pendingDeleteOrder
            ? `commande du ${formatPurchaseDate(pendingDeleteOrder.orderDate)}`
            : null);

    const deleteConfirmDialog = (
        <ConfirmDialog
            open={!!pendingDeleteOrderId}
            title="Supprimer la commande"
            message={
                pendingDeleteLabel
                    ? `Voulez-vous vraiment supprimer « ${pendingDeleteLabel} » ? Cette action est irréversible.`
                    : "Voulez-vous vraiment supprimer cette commande ? Cette action est irréversible."
            }
            confirmLabel="Supprimer"
            dangerous
            onConfirm={() => handleDeleteOrder(pendingDeleteOrderId)}
            onCancel={() => setPendingDeleteOrderId(null)}
        />
    );

    if (viewMode === "list") {
        return (
            <>
                <section className="card profile-section profile-section--catalog">
                    <div className="catalog-with-filters">
                        <OrderFilterToolbox
                            filters={draftFilters}
                            loading={loading}
                            onChange={setDraftFilters}
                            onApply={applyFilters}
                            onReset={resetFilters}
                        />

                        <div className="catalog-results">
                            <div className="catalog-results-header">
                                <h2>Commandes</h2>
                                {!loading && orders.length > 0 && (
                                    <p className="muted catalog-results-count admin-list-hint">
                                        {orders.length} commande{orders.length > 1 ? "s" : ""} affichée{orders.length > 1 ? "s" : ""}
                                        {" · menu ⋮ pour voir, modifier ou supprimer"}
                                    </p>
                                )}
                            </div>

                            {loading && <p className="muted">Chargement des commandes...</p>}
                            {detailLoading && <p className="muted">Chargement de la commande...</p>}

                            {!loading && orders.length === 0 && (
                                <p className="muted">Aucune commande ne correspond à votre recherche.</p>
                            )}

                            {!loading && orders.length > 0 && (
                                <div className="admin-table purchase-list">
                                    {orders.map((order) => {
                                        const orderLabel = order.previewTitle || "Commande";

                                        return (
                                            <div key={order.orderId} className="admin-row admin-list-row purchase-list-row">
                                                <button
                                                    type="button"
                                                    className="admin-list-row-main purchase-list-row-main"
                                                    onClick={() => openOrderView(order.orderId)}
                                                    disabled={detailLoading}
                                                >
                                                    <div className="purchase-list-row-top">
                                                        <OrderThumbnail order={order} />
                                                        <div className="purchase-list-row-heading">
                                                            <strong>{orderLabel}</strong>
                                                            <p className="muted">
                                                                <span className={orderStatusClassName(order.status)}>
                                                                    {formatOrderStatus(order.status)}
                                                                </span>
                                                                {" · "}
                                                                <Price value={order.totalPrice} />
                                                            </p>
                                                        </div>
                                                        <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                                                    </div>
                                                    <p className="muted purchase-fulfillment">
                                                        {order.customerDisplayName || order.customerUsername}
                                                    </p>
                                                    <p className="muted purchase-row-date">
                                                        {formatPurchaseDate(order.orderDate)}
                                                    </p>
                                                </button>
                                                <RowActionsMenu
                                                    ariaLabel={`Actions pour ${orderLabel}`}
                                                    items={buildOrderMenuItems(order)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
                {deleteConfirmDialog}
            </>
        );
    }

    if (viewMode === "edit" && selectedOrder) {
        return (
            <>
                <section className="card profile-section profile-section--wide">
                    <div className="section-header">
                        <h2>Modifier la commande</h2>
                    </div>

                    <form onSubmit={saveOrder}>
                        <dl className="profile-detail-view profile-detail-view--compact">
                            <div className="profile-detail-row">
                                <dt>Client</dt>
                                <dd>{selectedOrder.customerDisplayName || selectedOrder.customerUsername}</dd>
                            </div>
                            <div className="profile-detail-row">
                                <dt>Date</dt>
                                <dd>{formatPurchaseDate(selectedOrder.orderDate)}</dd>
                            </div>
                            <div className="profile-detail-row">
                                <dt>Total</dt>
                                <dd><strong><Price value={editTotal} /></strong></dd>
                            </div>
                        </dl>

                        <label className="field">
                            Statut
                            <select
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                            >
                                {ORDER_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="field">
                            Mode de réception
                            <select
                                value={form.fulfillmentType}
                                onChange={(e) => setForm({ ...form, fulfillmentType: e.target.value })}
                            >
                                <option value="PICKUP">Retrait en magasin</option>
                                <option value="DELIVERY">Livraison à l&apos;adresse</option>
                            </select>
                        </label>
                        <label className="field">
                            {form.fulfillmentType === "DELIVERY" ? "Adresse de livraison" : "Lieu de retrait"}
                            <textarea
                                rows="3"
                                value={form.deliverySummary}
                                onChange={(e) => setForm({ ...form, deliverySummary: e.target.value })}
                                required
                            />
                        </label>

                        <h3 className="admin-order-edit-lines-title">Articles</h3>
                        <div className="order-lines admin-order-edit-lines">
                            {form.lines.map((line) => (
                                <section key={line.productId} className="order-line-card admin-order-edit-line">
                                    <ProductImageGallery
                                        className="product-gallery--compact"
                                        photoIds={line.photoId ? [line.photoId] : []}
                                        title={line.title}
                                        placeholder="Aucune image"
                                    />
                                    <div className="admin-order-edit-line-body">
                                        <h3>{line.title}</h3>
                                        <p className="muted"><Price value={line.unitPrice} /> / unité</p>
                                        <div className="order-line-qty-row">
                                            <label className="field field--qty">
                                                Quantité
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    inputMode="numeric"
                                                    value={line.quantity}
                                                    onChange={(e) => updateLineQuantity(line.productId, e.target.value)}
                                                    onBlur={() => commitLineQuantity(line.productId)}
                                                    required
                                                />
                                            </label>
                                            <p className="order-line-subtotal">
                                                Sous-total :{" "}
                                                <strong><Price unitPrice={line.unitPrice} quantity={line.quantity} /></strong>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-inline"
                                            onClick={() => removeLine(line.productId)}
                                            disabled={form.lines.length <= 1}
                                        >
                                            Retirer
                                        </button>
                                    </div>
                                    {needsStockAllocation && (
                                        <div className="admin-order-lot-alloc">
                                            <h4>{showCostPrice ? "Lots (prix de revient)" : "Lots"}</h4>
                                            <p className="muted">
                                                Alloué : {allocatedTotalForProduct(form.stockAllocations, line.productId)}
                                                {" / "}
                                                {line.quantity}
                                            </p>
                                            {lotsLoading && <p className="muted">Chargement des lots...</p>}
                                            {form.stockAllocations.map((row, index) => {
                                                if (row.productId !== line.productId) {
                                                    return null;
                                                }
                                                const selectedLot = (lotsByProduct[line.productId] || [])
                                                    .find((lot) => lot.id === row.lotId);
                                                const available = selectedLot ? lotAvailability(selectedLot) : null;
                                                return (
                                                    <div key={`alloc-${line.productId}-${index}`} className="admin-order-lot-alloc-row">
                                                        <label className="field">
                                                            Lot
                                                            <select
                                                                value={row.lotId}
                                                                onChange={(e) => updateAllocationRow(index, "lotId", e.target.value)}
                                                                required
                                                            >
                                                                <option value="">Choisir un lot</option>
                                                                {(lotsByProduct[line.productId] || []).map((lot) => {
                                                                    const count = lotAvailability(lot);
                                                                    return (
                                                                        <option
                                                                            key={lot.id}
                                                                            value={lot.id}
                                                                            disabled={count < 1}
                                                                        >
                                                                            {formatLotOptionLabel(lot, count, showCostPrice)}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                            {selectedLot && (
                                                                <span className="muted">
                                                                    {available === 1
                                                                        ? "1 article disponible dans ce lot"
                                                                        : `${available} articles disponibles dans ce lot`}
                                                                </span>
                                                            )}
                                                        </label>
                                                        <label className="field field--qty">
                                                            Qté
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max={available != null && available > 0 ? available : undefined}
                                                                step="1"
                                                                inputMode="numeric"
                                                                value={row.quantity}
                                                                onChange={(e) => updateAllocationRow(index, "quantity", e.target.value)}
                                                                onBlur={() => commitAllocationQuantity(index)}
                                                                required
                                                            />
                                                        </label>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-inline"
                                                            onClick={() => removeAllocationRow(index)}
                                                        >
                                                            Retirer
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-inline"
                                                onClick={() => addAllocationRow(line.productId)}
                                            >
                                                Ajouter un lot
                                            </button>
                                        </div>
                                    )}
                                </section>
                            ))}
                        </div>

                        <div className="admin-order-add-product">
                            <label className="field">
                                Ajouter un produit
                                <input
                                    type="search"
                                    value={productQuery}
                                    onChange={(e) => setProductQuery(e.target.value)}
                                    placeholder="Rechercher un produit (2 caractères min.)"
                                    autoComplete="off"
                                />
                            </label>
                            {productSearchLoading && <p className="muted">Recherche...</p>}
                            {!productSearchLoading && productResults.length > 0 && (
                                <ul className="admin-order-product-results">
                                    {productResults.map((product) => (
                                        <li key={product.id}>
                                            <button
                                                type="button"
                                                className="admin-order-product-result"
                                                onClick={() => addProductToOrder(product)}
                                            >
                                                <span>{product.title}</span>
                                                <span className="muted"><Price value={lowestProductPrice(product)} /></span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="profile-form-actions">
                            <button className="btn btn-primary btn-inline" type="submit" disabled={saving}>
                                {saving ? "Enregistrement..." : "Enregistrer"}
                            </button>
                            <button type="button" className="btn btn-secondary btn-inline" onClick={cancelEdit}>
                                Annuler
                            </button>
                        </div>
                    </form>
                </section>
                {deleteConfirmDialog}
            </>
        );
    }

    if (!selectedOrder) {
        return (
            <section className="card profile-section profile-section--wide">
                <p className="muted">Chargement de la commande...</p>
                <BackLink onClick={backToList} className="purchase-detail-back">
                    Retour à la liste
                </BackLink>
            </section>
        );
    }

    return (
        <>
            <section className="card profile-section profile-section--wide purchase-detail-card">
                <BackLink onClick={backToList} className="purchase-detail-back">
                    Retour à la liste
                </BackLink>

                <header className="purchase-detail-header section-header">
                    <div className="purchase-detail-meta">
                        <p className="muted purchase-detail-date">
                            {formatPurchaseDate(selectedOrder.orderDate)}
                        </p>
                        <p className="muted purchase-detail-item-count">
                            {selectedOrder.itemCount} article{selectedOrder.itemCount > 1 ? "s" : ""}
                        </p>
                    </div>
                    <RowActionsMenu
                        ariaLabel="Actions de la commande"
                        items={buildOrderMenuItems(selectedOrder, { includeView: false })}
                    />
                </header>

                <div className="purchase-detail-progress">
                    <OrderProgressTracker order={selectedOrder} variant="admin" />
                </div>

                <label className="field field--status admin-order-status-field">
                    Changer le statut
                    <select
                        value={selectedOrder.status}
                        onChange={(e) => handleStatusSelect(e.target.value)}
                        disabled={statusSaving || completePanelOpen}
                    >
                        {ORDER_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>

                {completePanelOpen && (
                    <div className="admin-order-complete-panel" role="region" aria-label="Terminer la commande">
                        <h3>Terminer la commande</h3>
                        <p className="muted">
                            Sélectionnez les lots{showCostPrice ? " (prix de revient)" : ""} pour chaque article, comme lors de l&apos;édition.
                        </p>
                        {lotsLoading && <p className="muted">Chargement des lots...</p>}
                        {(selectedOrder.lines || []).map((line) => (
                            <div key={line.lineId || line.productId} className="admin-order-lot-alloc">
                                <h4>{line.title}</h4>
                                <p className="muted">
                                    Alloué : {allocatedTotalForProduct(statusAllocations, line.productId)}
                                    {" / "}
                                    {line.quantity}
                                </p>
                                {statusAllocations.map((row, index) => {
                                    if (row.productId !== line.productId) {
                                        return null;
                                    }
                                    const selectedLot = (lotsByProduct[line.productId] || [])
                                        .find((lot) => lot.id === row.lotId);
                                    const available = selectedLot ? lotAvailability(selectedLot) : null;
                                    return (
                                        <div key={`status-alloc-${line.productId}-${index}`} className="admin-order-lot-alloc-row">
                                            <label className="field">
                                                Lot
                                                <select
                                                    value={row.lotId}
                                                    onChange={(e) => setStatusAllocations((prev) => prev.map((item, rowIndex) => (
                                                        rowIndex === index ? { ...item, lotId: e.target.value } : item
                                                    )))}
                                                    required
                                                >
                                                    <option value="">Choisir un lot</option>
                                                    {(lotsByProduct[line.productId] || []).map((lot) => {
                                                        const count = lotAvailability(lot);
                                                        return (
                                                            <option
                                                                key={lot.id}
                                                                value={lot.id}
                                                                disabled={count < 1}
                                                            >
                                                                {formatLotOptionLabel(lot, count, showCostPrice)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </label>
                                            <label className="field field--qty">
                                                Quantité
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={row.quantity}
                                                    onChange={(e) => setStatusAllocations((prev) => prev.map((item, rowIndex) => (
                                                        rowIndex === index
                                                            ? { ...item, quantity: e.target.value }
                                                            : item
                                                    )))}
                                                    required
                                                />
                                            </label>
                                            {available != null && (
                                                <p className="muted">Disponible : {available}</p>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-inline"
                                                onClick={() => setStatusAllocations((prev) => prev.filter((_, rowIndex) => rowIndex !== index))}
                                            >
                                                Retirer
                                            </button>
                                        </div>
                                    );
                                })}
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-inline"
                                    onClick={() => {
                                        const lots = lotsByProduct[line.productId] || [];
                                        const defaultLot = lots.find((lot) => lotAvailability(lot) > 0) || lots[0];
                                        setStatusAllocations((prev) => [
                                            ...prev,
                                            {
                                                productId: line.productId,
                                                lotId: defaultLot?.id || "",
                                                quantity: 1
                                            }
                                        ]);
                                    }}
                                >
                                    Ajouter un lot
                                </button>
                            </div>
                        ))}
                        <div className="profile-form-actions">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={confirmCompleteWithAllocations}
                                disabled={statusSaving || lotsLoading}
                            >
                                {statusSaving ? "Enregistrement..." : "Confirmer Terminée"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={closeCompletePanel}
                                disabled={statusSaving}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                )}

                <dl className="profile-detail-view">
                    <div className="profile-detail-row">
                        <dt>Client</dt>
                        <dd>{selectedOrder.customerDisplayName || selectedOrder.customerUsername}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Nom d&apos;utilisateur</dt>
                        <dd>{selectedOrder.customerUsername}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Montant total</dt>
                        <dd><strong><Price value={selectedOrder.totalPrice} /></strong></dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Mode de réception</dt>
                        <dd>{formatFulfillmentType(selectedOrder.fulfillmentType)}</dd>
                    </div>
                    {selectedOrder.deliverySummary && (
                        <div className="profile-detail-row">
                            <dt>{selectedOrder.fulfillmentType === "DELIVERY" ? "Adresse" : "Lieu"}</dt>
                            <dd>{selectedOrder.deliverySummary}</dd>
                        </div>
                    )}
                    {selectedOrder.customerComment && (
                        <div className="profile-detail-row">
                            <dt>Commentaire client</dt>
                            <dd>{selectedOrder.customerComment}</dd>
                        </div>
                    )}
                    {selectedOrder.receiptNumber && (
                        <div className="profile-detail-row">
                            <dt>N° de reçu</dt>
                            <dd>{selectedOrder.receiptNumber}</dd>
                        </div>
                    )}
                </dl>

                <div className="order-lines">
                    {selectedOrder.lines?.map((line) => (
                        <section key={line.lineId} className="order-line-card">
                            <ProductImageGallery
                                className="product-gallery--compact"
                                photoIds={line.photoIds?.length ? line.photoIds : (line.photoId ? [line.photoId] : [])}
                                title={line.title}
                                placeholder="Aucune image"
                            />
                            <div>
                                <h3>{line.title}</h3>
                                <p className="muted">{line.category}</p>
                                {line.description && <p>{line.description}</p>}
                                <p>
                                    {line.quantity} × <Price value={line.unitPrice} /> ={" "}
                                    <strong><Price unitPrice={line.unitPrice} quantity={line.quantity} /></strong>
                                </p>
                                {selectedOrder.stockAllocations?.filter((row) => row.productId === line.productId).length > 0 && (
                                    <ul className="muted admin-order-lot-view">
                                        {selectedOrder.stockAllocations
                                            .filter((row) => row.productId === line.productId)
                                            .map((row) => (
                                                <li key={`${row.lotId}-${row.quantity}`}>
                                                    Lot {row.lotName || row.lotId}
                                                    {" · "}
                                                    qté {row.quantity}
                                                    {showCostPrice && (
                                                        <>
                                                            {" · "}
                                                            prix de revient <Price value={row.costPrice ?? 0} />
                                                        </>
                                                    )}
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            </section>
            {deleteConfirmDialog}
        </>
    );
}
